import React, { useState } from 'react';
import { searchNearbyPlaces } from '@/services/placesAPI';
import { batchSaveGooglePlaces, testDatabaseConnection } from '@/services/enhancedCampDatabaseService';
import { testTransformation } from '@/utils/googlePlacesTransform';

const GooglePlacesIntegrationTest = () => {
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const addResult = (test, status, message) => {
    setResults(prev => [...prev, { test, status, message, timestamp: new Date().toISOString() }]);
  };

  const runAllTests = async () => {
    setResults([]);
    setLoading(true);
    setError(null);

    try {
      // Test 1: Database Connection
      addResult('Database Connection', 'testing', 'Testing database connection and schema...');
      const dbConnected = await testDatabaseConnection();
      if (dbConnected) {
        addResult('Database Connection', 'success', 'Database connection successful');
      } else {
        addResult('Database Connection', 'error', 'Database connection failed');
        return;
      }

      // Test 2: Data Transformation
      addResult('Data Transformation', 'testing', 'Testing Google Places data transformation...');
      try {
        const testResult = testTransformation();
        addResult('Data Transformation', 'success', 'Transformation test successful');
      } catch (error) {
        addResult('Data Transformation', 'error', `Transformation failed: ${error.message}`);
        return;
      }

      // Test 3: Google Places API Call
      addResult('Google Places API', 'testing', 'Testing Google Places API call...');
      const testLocation = { lat: 33.7490, lng: -84.3880 }; // Atlanta
      const apiResults = await searchNearbyPlaces(testLocation, 5);
      
      if (apiResults && apiResults.length > 0) {
        addResult('Google Places API', 'success', `API returned ${apiResults.length} results`);
        
        // Log first result for inspection
        console.log('📋 First API result sample:', apiResults[0]);
        
        // Test 4: Database Save
        addResult('Database Save', 'testing', 'Testing database save with API results...');
        
        // Take first 3 results for testing
        const testResults = apiResults.slice(0, 3);
        
        const saveResult = await batchSaveGooglePlaces(testResults);
        
        if (saveResult.summary.successful > 0) {
          addResult('Database Save', 'success', 
            `Successfully saved ${saveResult.summary.successful}/${saveResult.summary.total} records`);
          
          if (saveResult.errors.length > 0) {
            addResult('Database Save', 'warning', 
              `${saveResult.errors.length} errors encountered: ${saveResult.errors.map(e => e.error).join(', ')}`);
          }
        } else {
          addResult('Database Save', 'error', 'Failed to save any records');
        }
        
      } else {
        addResult('Google Places API', 'error', 'No results returned from API');
      }

      // Test 5: Data Structure Validation
      addResult('Data Structure', 'testing', 'Validating data structure consistency...');
      const sampleResult = apiResults[0];
      if (sampleResult) {
        const requiredFields = ['place_id', 'name', 'location', 'rating'];
        const missingFields = requiredFields.filter(field => !sampleResult[field]);
        
        if (missingFields.length === 0) {
          addResult('Data Structure', 'success', 'All required fields present');
        } else {
          addResult('Data Structure', 'warning', `Missing fields: ${missingFields.join(', ')}`);
        }
      }

    } catch (error) {
      console.error('Test suite error:', error);
      setError(error.message);
      addResult('Test Suite', 'error', `Test suite failed: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const clearResults = () => {
    setResults([]);
    setError(null);
  };

  return (
    <div className="max-w-4xl mx-auto p-6 bg-white rounded-lg shadow-lg">
      <h2 className="text-2xl font-bold mb-6 text-gray-900">
        🧪 Google Places Integration Test
      </h2>
      
      <div className="mb-6 space-x-4">
        <button
          onClick={runAllTests}
          disabled={loading}
          className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed font-medium"
        >
          {loading ? 'Running Tests...' : 'Run All Tests'}
        </button>
        
        <button
          onClick={clearResults}
          className="px-6 py-3 bg-gray-500 text-white rounded-lg hover:bg-gray-600 font-medium"
        >
          Clear Results
        </button>
      </div>

      {error && (
        <div className="mb-6 p-4 bg-red-100 border border-red-300 rounded-lg">
          <h3 className="font-semibold text-red-800">Test Suite Error</h3>
          <p className="text-red-700">{error}</p>
        </div>
      )}

      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-gray-900">Test Results</h3>
        
        {results.length === 0 ? (
          <p className="text-gray-500 italic">No test results yet. Click "Run All Tests" to begin.</p>
        ) : (
          results.map((result, index) => (
            <div
              key={index}
              className={`p-4 rounded-lg border-l-4 ${
                result.status === 'success'
                  ? 'bg-green-50 border-green-400'
                  : result.status === 'error'
                  ? 'bg-red-50 border-red-400'
                  : result.status === 'warning'
                  ? 'bg-yellow-50 border-yellow-400'
                  : 'bg-blue-50 border-blue-400'
              }`}
            >
              <div className="flex items-center justify-between">
                <h4 className="font-medium text-gray-900">{result.test}</h4>
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                  result.status === 'success'
                    ? 'bg-green-100 text-green-800'
                    : result.status === 'error'
                    ? 'bg-red-100 text-red-800'
                    : result.status === 'warning'
                    ? 'bg-yellow-100 text-yellow-800'
                    : 'bg-blue-100 text-blue-800'
                }`}>
                  {result.status}
                </span>
              </div>
              <p className="mt-2 text-sm text-gray-700">{result.message}</p>
              <p className="mt-1 text-xs text-gray-500">
                {new Date(result.timestamp).toLocaleTimeString()}
              </p>
            </div>
          ))
        )}
      </div>

      <div className="mt-8 p-4 bg-gray-50 rounded-lg">
        <h4 className="font-semibold text-gray-900 mb-2">Test Coverage</h4>
        <ul className="text-sm text-gray-700 space-y-1">
          <li>✅ Database connection and schema validation</li>
          <li>✅ Google Places API data transformation</li>
          <li>✅ Google Places API integration</li>
          <li>✅ Database save operations</li>
          <li>✅ Data structure validation</li>
          <li>✅ Error handling and reporting</li>
        </ul>
      </div>
    </div>
  );
};

export default GooglePlacesIntegrationTest;
